/**
 * Where a thumbnail's bytes come from.
 *
 * Two homes, one rule: a file of the media library is served by the gateway
 * (`GET /media/{id}/file`, ETag, a year of cache, behind the session cookie);
 * anything else is a file of the public site and is read from its own domain.
 * There is no resized variant on either side — resizing needs an image
 * service, and the free plan has none — so the original is shown at a fixed
 * size (see document/62, "Miniature"). The library files are typically
 * already web-sized (webp, ≤ 10 MB cap), and the browser caches them for a year.
 */

import { libraryPathFromValue } from './media.js'
import { liveUrl } from './live-url.js'

export interface ThumbnailRef {
  path: string
  assetId?: string | null
}

export interface ThumbnailContext {
  /** The site's primary domain, for files that live on the public site. */
  domain: string | null | undefined
  /** `mediaFileUrl`; injected so this stays pure. */
  fileUrl: (assetId: string) => string
}

/** The address to put in `<img src>`, or `null` when there is nothing sensible to load. */
export function thumbnailSource(image: ThumbnailRef | null | undefined, context: ThumbnailContext): string | null {
  const path = image?.path?.trim()
  if (!image || !path) return null
  if (image.assetId) return context.fileUrl(image.assetId)
  if (/^https?:\/\//i.test(path)) return path
  // A library path whose asset the gateway could not find is still a file of
  // the public site once published: the site serves `/media/...` too.
  return liveUrl(context.domain, path.startsWith('/') ? path : `/${path}`)
}

/** True when the address is in the library namespace, so an asset lookup by path can find it. */
export function isLibraryAddress(path: string | null | undefined): boolean {
  return libraryPathFromValue(path) !== null
}

export type ThumbSize = 'sm' | 'md' | 'lg'
